import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, CheckCircle2, PauseCircle, PlayCircle, Clock } from "lucide-react";
import { FunnelHealth } from "@/components/FunnelHealth";

const dot = (color) => ({
  red: "bg-red-500", amber: "bg-amber-400", green: "bg-emerald-400", gray: "bg-neutral-600",
}[color]);

function AttentionCard({ label, value, to, color, testid }) {
  const c = value > 0 ? color : "gray";
  return (
    <Link to={to} data-testid={testid}
      className={`block border bg-appcard p-4 transition-colors hover:border-volt ${
        c === "red" ? "border-red-800" : c === "amber" ? "border-amber-700/60" : "border-neutral-800"
      }`}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dot(c)}`} />
        <span className="text-[10px] tracking-widest text-neutral-500">{label}</span>
      </div>
      <div className={`heading text-3xl mt-1 ${c === "red" ? "text-red-400" : c === "amber" ? "text-amber-400" : c === "green" ? "text-emerald-400" : "text-neutral-400"}`}>{value}</div>
    </Link>
  );
}

function CampaignSummary({ c, testid }) {
  const running = c.status === "running";
  return (
    <Link to="/hq/campaigns" data-testid={testid}
      className={`block border bg-appcard p-4 hover:border-volt transition-colors ${running ? "border-emerald-800/60" : "border-amber-700/60"}`}>
      <div className="flex items-center gap-2">
        {running ? <PlayCircle size={15} className="text-emerald-400" /> : <PauseCircle size={15} className="text-amber-400" />}
        <span className="heading text-sm">{c.name}</span>
        <span className={`ml-auto text-[9px] tracking-widest px-1.5 py-0.5 border ${running ? "border-emerald-600 text-emerald-400" : "border-amber-600 text-amber-400"}`}>
          {running ? "RUNNING" : "PAUSED"}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2 text-center">
        {[["Queued", c.queued], ["Ready", c.ready], ["Sent today", `${c.sent_today}/${c.daily_limit}`], ["Blocked", c.blocked]].map(([l, v]) => (
          <div key={l}>
            <div className="heading text-lg">{v}</div>
            <div className="text-[9px] tracking-widest text-neutral-500">{l.toUpperCase()}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-3 text-[10px] text-neutral-500">
        <Clock size={11} />
        <span>Last run: {c.last_run ? new Date(c.last_run).toLocaleString() : "never"}</span>
        <span>· Next: {c.next_run ? new Date(c.next_run).toLocaleString() : "—"}</span>
      </div>
    </Link>
  );
}

export default function AdminHome() {
  const [att, setAtt] = useState(null);
  const [camps, setCamps] = useState(null);
  const [days, setDays] = useState(7);

  useEffect(() => {
    api.get(`/admin/attention?days=${days}`).then(({ data }) => setAtt(data)).catch(() => {});
  }, [days]);
  useEffect(() => {
    api.get("/admin/campaigns/overview").then(({ data }) => setCamps(data)).catch(() => {});
  }, []);

  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl" data-testid="hq-home-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-widest text-volt">ADMIN HQ · OVERVIEW</div>
          <h1 className="heading text-4xl mt-1">WHAT NEEDS ATTENTION</h1>
        </div>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`home-range-${d}`} onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-[10px] tracking-widest border ${days === d ? "border-blaze text-blaze" : "border-neutral-700 text-neutral-500 hover:border-neutral-500"}`}>
              {d}D
            </button>
          ))}
        </div>
      </div>

      {/* Today funnel health */}
      <FunnelHealth />

      {/* Stalled campaign detector */}
      {att?.stalled_campaigns?.length > 0 && (
        <div data-testid="stalled-campaigns-banner" className="mt-6 border border-red-700 bg-red-950/40 p-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-red-400 shrink-0 mt-0.5" />
          <div className="text-xs leading-relaxed">
            <span className="heading text-sm text-red-400">CAMPAIGN STALLED — QUEUE NOT MOVING</span>
            {att.stalled_campaigns.map((s) => (
              <p key={s.job_id} className="text-neutral-400 mt-1">
                <span className="text-white">{s.label}</span>: {s.ready} ready but no completed run
                {s.last_ok_run ? ` since ${new Date(s.last_ok_run).toLocaleString()}` : " on record"}.
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Global kill switch */}
      {att?.email_quota?.paused && (
        <div data-testid="kill-switch-banner" className="mt-6 border border-red-700 bg-red-950/40 p-4 flex items-start gap-3">
          <PauseCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
          <div className="text-xs leading-relaxed">
            <span className="heading text-sm text-red-400">GLOBAL EMAIL KILL SWITCH — ON</span>
            <p className="text-neutral-400 mt-1">Every non-essential email (campaigns, sweeps, nudges, digests) is blocked platform-wide regardless of individual campaign toggles.</p>
          </div>
        </div>
      )}

      {/* Email quota circuit breaker */}
      {att?.email_quota?.alert && (
        <div data-testid="quota-alert-banner"
          className={`mt-6 border p-4 flex items-start gap-3 ${att.email_quota.exhausted ? "border-red-700 bg-red-950/40" : "border-amber-700 bg-amber-950/30"}`}>
          <AlertTriangle size={16} className={`shrink-0 mt-0.5 ${att.email_quota.exhausted ? "text-red-400" : "text-amber-400"}`} />
          <div className="text-xs leading-relaxed">
            <span className={`heading text-sm ${att.email_quota.exhausted ? "text-red-400" : "text-amber-400"}`}>
              {att.email_quota.exhausted ? "DAILY EMAIL QUOTA REACHED — SENDING HARD-STOPPED" : "EMAIL QUOTA WARNING"}
            </span>
            <p className="text-neutral-400 mt-1">
              {att.email_quota.sent_today}/{att.email_quota.quota} emails sent today ({att.email_quota.pct}%).
              {att.email_quota.exhausted
                ? " All non-essential sending is blocked until midnight UTC."
                : " Sending hard-stops automatically at 100%. Pause campaigns if this is unexpected."}
            </p>
          </div>
        </div>
      )}

      {/* Campaign health */}
      <div className="mt-6 grid md:grid-cols-2 gap-3" data-testid="home-campaigns">
        {camps ? (
          <>
            <CampaignSummary c={camps.coach_claim} testid="home-campaign-coach-claim" />
            <CampaignSummary c={camps.vacancy_outreach} testid="home-campaign-vacancy" />
            {camps.monthly_update && <CampaignSummary c={camps.monthly_update} testid="home-campaign-monthly" />}
          </>
        ) : (
          <div className="text-neutral-500 text-sm py-6">Loading campaign status…</div>
        )}
      </div>

      {/* Needs attention grid */}
      {!att ? (
        <div className="mt-6 text-neutral-500 text-sm">Loading…</div>
      ) : (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="needs-attention-panel">
          <AttentionCard label="READY FOR APPROVAL" value={att.ready_for_approval} to="/hq/queue?bucket=ready" color="amber" testid="att-ready" />
          <AttentionCard label="MISSING RECIPIENT EMAIL" value={att.missing_email} to="/hq/queue?bucket=needs_email" color="amber" testid="att-missing-email" />
          <AttentionCard label="AWAITING SPORT VERIFICATION" value={att.needs_sport_verification} to="/hq/queue?bucket=needs_sport" color="amber" testid="att-sport" />
          <AttentionCard label="QUARANTINED · NON-LACROSSE" value={att.quarantined_non_lacrosse} to="/hq/queue?bucket=quarantined" color="red" testid="att-quarantined" />
          <AttentionCard label="BLOCKED (OTHER)" value={att.blocked_other} to="/hq/queue?bucket=blocked" color="amber" testid="att-blocked" />
          <AttentionCard label="BOUNCED / FAILED ADDRESSES" value={att.bounced_or_failed} to="/hq/data-quality?cat=bounced" color="red" testid="att-bounced" />
          <AttentionCard label="DUPLICATE COACHES" value={att.duplicate_coaches} to="/hq/data-quality?cat=duplicate_coaches" color="amber" testid="att-dup-coaches" />
          <AttentionCard label="DUPLICATE JOBS" value={att.duplicate_jobs} to="/hq/data-quality?cat=duplicate_jobs" color="amber" testid="att-dup-jobs" />
          <AttentionCard label="AUTOMATION FAILURES" value={att.automation_failures} to="/hq/system" color="red" testid="att-failures" />
          <AttentionCard label={`ACCOUNTS CREATED · ${days}D`} value={att.accounts_created} to="/hq/coaches?claim_state=account" color="green" testid="att-accounts" />
        </div>
      )}

      <div className="mt-8 border border-neutral-800 bg-appcard p-4 flex items-start gap-3">
        <CheckCircle2 size={16} className="text-volt shrink-0 mt-0.5" />
        <p className="text-xs text-neutral-400 leading-relaxed">
          Every count above is clickable and opens the exact records behind the number.
          <span className="text-neutral-500"> Red = broken or requires intervention · Amber = waiting on your review · Green = healthy · Gray = nothing to do.</span>
        </p>
      </div>
    </div>
  );
}
